import React, { useState } from 'react';
import Layout from 'layouts/manager/Layout';
import Pagination from 'components/Pagination';
import Table, { TableColumn } from 'components/Table';

const tableData = [
  {
    name: '東京本店',
    address: '東京都千代田区1-1-1',
    tel: '03-1234-5678',
    member_no: '0001',
    company: '株式会社A',
    status: 'active',
  },
  {
    name: '大阪支店',
    address: '大阪府大阪市北区2-2-2',
    tel: '06-2345-6789',
    member_no: '0002',
    company: '株式会社B',
    status: 'inactive',
  },
  {
    name: '名古屋営業所',
    address: '愛知県名古屋市中区3-3-3',
    tel: '052-345-6789',
    member_no: '0003',
    company: '株式会社C',
    status: 'active',
  },
];

type TableRow = {
  name: string;
  address: string;
  tel: string;
  member_no: string;
  company: string;
  status: string;
};

const columns: TableColumn<TableRow>[] = [
  { key: 'member_no', label: '会員番号', render: (row) => (
    <button type="button" className="text-blue-700 hover:underline">{row.member_no || '-'}</button>
  ) },
  { key: 'name', label: '店舗名' },
  { key: 'address', label: '店舗住所' },
  { key: 'company', label: '会社名', render: (row) => row.company || '-' },
  { key: 'status', label: 'ステータス', render: (row) => row.status === 'active' ? '有効' : '無効' },
];

const actionLinks = [
  { label: '編集', color: 'text-green-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/member/edit/${row.member_no}` },
  { label: '削除', color: 'text-red-600', hover: 'hover:underline', onClick: (row: TableRow) => { if(window.confirm(`${row.name} を削除します。よろしいですか？`)){ window.location.href = `/manager/member/delete/${row.member_no}`; } } },
  { label: '印刷', color: 'text-gray-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/member/print/${row.member_no}` },
  { label: 'レスキュー', color: 'text-indigo-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/member/rescue/${row.member_no}` },
  { label: 'ログイン', color: 'text-orange-700', hover: 'hover:underline', href: (row: TableRow) => `/manager/member/login/${row.member_no}` },
];

type SortKey = 'member_no' | 'name' | 'address' | 'company' | 'status';
type SortOrder = 'asc' | 'desc';

const Search: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState<number[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('member_no');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // 検索フォーム用の初期値
  const [search, setSearch] = useState({
    manager: '',
    member_no: '',
    store: '',
    tel: '',
    address: '',
    email: '',
    user_id: '',
    app_payment: '',
    payment_term: '',
    contract_no: '',
    company: '',
    template: '',
    visit_rule: '',
    status: '',
  });

  // ページネーション用
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedData = [...tableData].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  }).slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // チェックボックス
  const handleCheck = (idx: number) => {
    setChecked((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const handleCheckAll = () => {
    if (checked.length === tableData.length) {
      setChecked([]);
    } else {
      setChecked(tableData.map((_, idx) => idx));
    }
  };

  // ソートリンク
  const renderSortLink = (key: SortKey, label: string) => (
    <button
      type="button"
      className="flex items-center text-xs font-medium text-gray-700 hover:underline focus:outline-none"
      onClick={() => {
        if (sortKey === key) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortKey(key);
          setSortOrder('asc');
        }
      }}
    >
      {label}
      {sortKey === key && (
        <span className="ml-1 text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );

  return (
    <Layout>
      <div className="mx-auto">
        {/* タイトル */}
        <h1 className="mb-3 text-lg font-bold text-gray-800">店舗管理</h1>

        {/* 検索項目（開閉式） */}
        <div className="mb-6">
          <button
            className="flex items-center w-full px-4 py-2 rounded-t-lg"
            style={{ backgroundColor: '#f7fafd' }}
            onClick={() => setOpen((v) => !v)}
            type="button"
          >
            <span
              className="mr-2 text-lg transition-transform"
              style={{
                color: '#002c6a',
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            >
              ▼
            </span>
            <span
              className="flex-1 text-base font-bold text-left"
              style={{ color: '#002c6a' }}
            >
              検索
            </span>
          </button>
          {open && (
            <div className="p-4 bg-white border rounded-b-lg shadow">
              <form className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">担当者名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="店舗担当者名を入力" value={search.manager} onChange={e => setSearch({ ...search, manager: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会員番号</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="会員番号を入力" value={search.member_no} onChange={e => setSearch({ ...search, member_no: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">店舗名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="店舗名を入力" value={search.store} onChange={e => setSearch({ ...search, store: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">電話番号</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="電話番号を入力" value={search.tel} onChange={e => setSearch({ ...search, tel: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">住所</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="住所を入力" value={search.address} onChange={e => setSearch({ ...search, address: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">担当メールアドレス</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="担当メールアドレスを入力" value={search.email} onChange={e => setSearch({ ...search, email: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">ログインID</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="ログインIDを入力" value={search.user_id} onChange={e => setSearch({ ...search, user_id: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">アプリ決済</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.app_payment} onChange={e => setSearch({ ...search, app_payment: e.target.value })}>
                    <option value="">アプリ決済を選択</option>
                    <option value="1">有</option>
                    <option value="0">無</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">支払条件</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.payment_term} onChange={e => setSearch({ ...search, payment_term: e.target.value })}>
                    <option value="">支払条件を選択</option>
                    <option value="1">末締めの翌月末銀行振込</option>
                    <option value="9">その他</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">契約No</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="契約Noを入力" value={search.contract_no} onChange={e => setSearch({ ...search, contract_no: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">会社名</label>
                  <input type="text" className="w-full px-2 py-1 border rounded" placeholder="会社名を入力" value={search.company} onChange={e => setSearch({ ...search, company: e.target.value })} />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">テンプレート</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.template} onChange={e => setSearch({ ...search, template: e.target.value })}>
                    <option value="">テンプレートを選択</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">訪問ルール</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.visit_rule} onChange={e => setSearch({ ...search, visit_rule: e.target.value })}>
                    <option value="">訪問ルールを選択</option>
                    <option value="1">ルール1</option>
                    <option value="2">ルール2</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">ステータス</label>
                  <select className="w-full px-2 py-1 border rounded" value={search.status} onChange={e => setSearch({ ...search, status: e.target.value })}>
                    <option value="">ステータスを選択</option>
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                  </select>
                </div>
                <div className="flex justify-start mt-2 md:col-span-3">
                  <button
                    type="submit"
                    className="px-3 py-1 text-sm transition rounded"
                    style={{ color: '#ffffff', background: '#002c6a' }}
                  >
                    検索
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* 一覧テーブル（枠で囲み、上部にボタン） */}
        <div className="p-4 bg-white border rounded shadow">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1 text-xs font-semibold rounded"
                style={{ background: '#002c6a', color: '#fff' }}
                onClick={() => { window.location.href = '/manager/member/add'; }}
              >
                会員追加
              </button>
              <button
                className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
                style={{ color: '#002c6a', background: '#f7fafd' }}
                type="button"
              >
                CSVインポート
              </button>
              <button
                className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
                style={{ color: '#002c6a', background: '#f7fafd' }}
                type="button"
              >
                CSVエクスポート
              </button>
            </div>
          </div>
          <Table
            data={pagedData}
            columns={columns}
            checked={checked}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            showCheckbox
            actions={actionLinks}
            renderSortLink={renderSortLink}
          />
          {/* ページネーション */}
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>
    </Layout>
  );
};

export default Search;
